import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { User } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';

// Questa pagina viene raggiunta in due modi diversi, entrambi col contratto
// token/user (o error) in query string:
// 1. Popup del login social (window.opener presente) → propaga i dati alla
//    finestra madre via postMessage e si chiude.
// 2. Pagina intera, aperta cliccando un link ricevuto per email (verifica
//    email o accesso free senza password) → non c'è nessun opener: qui
//    dobbiamo loggare noi stessi l'utente e portarlo in dashboard.
const roleRedirect: Record<string, string> = {
  superadmin: '/superadmin',
  admin: '/admin',
  user: '/dashboard',
};

const SocialCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    const userParam = searchParams.get('user');
    const error = searchParams.get('error');
    const verified = searchParams.get('verified');

    let message: { token?: string; user?: User; error?: string };

    try {
      if (error) {
        message = { error };
      } else if (token && userParam) {
        message = {
          token,
          user: JSON.parse(decodeURIComponent(userParam)) as User,
        };
      } else {
        message = { error: 'Parametri mancanti nella risposta del server.' };
      }
    } catch {
      message = { error: 'Errore durante il parsing dei dati utente.' };
    }

    if (window.opener) {
      window.opener.postMessage(message, window.location.origin);
      window.close();
      return;
    }

    if (message.error || !message.token || !message.user) {
      setPageError(
        message.error ?? 'Parametri mancanti nella risposta del server.'
      );
      return;
    }

    loginWithToken(message.user, message.token);
    const target = roleRedirect[message.user.role] ?? '/dashboard';
    navigate(verified ? `${target}?verified=${verified}` : target, {
      replace: true,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (pageError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'sans-serif',
          gap: '1rem',
          color: '#64748b',
          textAlign: 'center',
          padding: '0 1rem',
        }}
      >
        <p style={{ fontSize: '0.95rem', margin: 0 }}>{pageError}</p>
        <a href="/" style={{ color: '#6366f1', fontSize: '0.9rem' }}>
          Torna alla home
        </a>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        fontFamily: 'sans-serif',
        gap: '1rem',
        color: '#64748b',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          border: '3px solid #e2e8f0',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <p style={{ fontSize: '0.9rem', margin: 0 }}>Accesso in corso…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SocialCallback;
